import { Link } from '@tanstack/react-router';
import { Tag } from '@/components/Tag';
import type { JobChangeProcess } from '../../job_change_processes/types/JobChangeProcess';
import DashboardOrganizationUserSelect from './DashboardOrganizationUserSelect';
import DashboardEmptyState from './DashboardEmptyState';

type Props = {
  jobChangeProcesses: JobChangeProcess[];
  isLoading: boolean;
  /** 担当者 Select の値（ALL_USERS_VALUE = 全担当者） */
  organizationUserValue: string;
  onOrganizationUserChange: (value: string) => void;
};

const PROGRESS_LABEL: Record<JobChangeProcess['current_progress'], string> = {
  none: '未着手',
  document_screening: '書類選考',
  meeting: 'CA面談',
  '1st_interview': '一次選考',
  '2nd_interview': '二次選考',
  '3rd_interview': '三次選考',
  '4th_interview': '四次選考',
  final_interview: '最終選考',
  offer_meeting: 'オファー面談',
  offered: '内定',
  closed: '決定',
};

function formatTime(date: Date): string {
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

/**
 * - ヘッダ: 「本日の予定」 + 件数 Tag + 担当者 Select
 * - 各行: 時刻 / 候補者 × 企業（リンク）/ 進捗ステータスタグ（グレー）
 */
export default function DashboardTodaySchedule({
  jobChangeProcesses,
  isLoading,
  organizationUserValue,
  onOrganizationUserChange,
}: Props) {
  return (
    <div className="flex flex-col gap-xs rounded-sm bg-white p-md shadow-[0_0_3px_0_rgba(0,0,0,0.2)]">
      <div className="flex items-center justify-between gap-md">
        <div className="flex items-center gap-sm">
          <h2 className="text-sm leading-normal font-bold text-[#101828]">本日の予定</h2>
          <Tag color="blue" size="md" className="leading-none">
            {jobChangeProcesses.length}件
          </Tag>
        </div>
        <DashboardOrganizationUserSelect
          value={organizationUserValue}
          onChange={onOrganizationUserChange}
        />
      </div>

      {isLoading ? (
        <ul className="flex h-[246px] flex-col px-sm">
          {Array.from({ length: 3 }).map((_, i) => (
            <li
              key={i}
              className="flex items-center gap-md border-b border-solid border-border px-xs py-md last:border-b-0"
            >
              <span className="h-5 w-[38px] shrink-0 animate-pulse rounded-sm bg-medium-gray" />
              <span className="h-5 flex-1 animate-pulse rounded-sm bg-medium-gray" />
              <span className="h-6 w-[90px] shrink-0 animate-pulse rounded-sm bg-medium-gray" />
            </li>
          ))}
        </ul>
      ) : jobChangeProcesses.length === 0 ? (
        <DashboardEmptyState className="min-h-[77px]" />
      ) : (
        <div className="hidden-scrollbar flex h-[246px] flex-col overflow-y-auto rounded-sm border border-solid border-border bg-white px-sm">
          <ul className="flex flex-col">
            {jobChangeProcesses.map((j) => {
              const interviewedAt = j.latest_log?.interviewed_at;
              const date = interviewedAt ? new Date(interviewedAt) : null;
              const jobPostUrl = j.provided_job_post?.urls?.show;
              // マッチング管理タブを開くために search params を付与する
              const candidateMatchingUrl: string = `${j.candidate_profile.url}?tab=matching`;
              return (
                <li
                  key={j.id}
                  className="flex items-center gap-md border-b border-solid border-border px-xs py-md last:border-b-0"
                >
                  <span className="w-[38px] shrink-0 text-sm leading-normal whitespace-nowrap text-main">
                    {date ? formatTime(date) : '—'}
                  </span>
                  <div className="flex min-w-0 flex-1 items-center gap-xxs text-xs leading-[1.7]">
                    <Link to={candidateMatchingUrl} className="shrink-0 text-link">
                      {j.candidate_profile.name}
                    </Link>
                    <span className="shrink-0 text-main">×</span>
                    {j.company?.name &&
                      (jobPostUrl ? (
                        <Link to={jobPostUrl} className="truncate text-link">
                          {j.company.name}
                        </Link>
                      ) : (
                        <span className="truncate text-link">{j.company.name}</span>
                      ))}
                  </div>
                  <span className="inline-flex h-6 shrink-0 items-center justify-center rounded-sm bg-tag-gray px-sm text-xs leading-none font-bold whitespace-nowrap text-tag-gray">
                    {PROGRESS_LABEL[j.current_progress] ?? j.progress_name}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
